/**
 * These are the bindings that will take place on the frictionless checkout page when the
 * page load event occurs.
 */
import store from 'store/Store';
import anaUtils from 'analytics/utils';
import userUtils from 'utils/User';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import anaConsts from 'analytics/constants';
import orderUtils from 'utils/Order';
import { BASKET_TYPES } from 'constants/RwdBasket';
import DeliveryOptionsUtils from 'utils/DeliveryOptions';

// Returns 1, 2, or 3 based on which Fast Delivery methods are available at the user's zip.
// Returns null when there is no SameDay shipping group (no Fast Delivery items in basket).
function getFastDeliveryAvailableValue() {
    const orderDetails = store.getState().order?.orderDetails;

    if (!orderDetails) {
        return null;
    }

    const sameDayShippingGroup = orderUtils.getSameDayShippingGroup(orderDetails);

    if (!sameDayShippingGroup) {
        return null;
    }

    const availableMethods = sameDayShippingGroup.availableShippingMethods || [];
    // 'Same Day' matches both 'Same Day Delivery' and 'Free Rouge Same Day Delivery'
    const isSddAvailable = availableMethods.some(m => m.shippingMethodType?.includes('Same Day'));
    // NDD shippingMethodType from API contains 'Next Day'
    const isNddAvailable = availableMethods.some(m => m.shippingMethodType?.includes('Next Day'));

    if (isSddAvailable && isNddAvailable) {
        return 3;
    }

    if (isNddAvailable) {
        return 2;
    }

    if (isSddAvailable) {
        return 1;
    }

    return null;
}

// Builds s.products string with event323 bound only to Fast Delivery SKUs.
// Get It Shipped SKUs appear in the string with eVar26 only (no event323 per spec).
function buildFastDeliveryAvailableProductString(availableValue) {
    const { itemsByBasket } = store.getState().basket;
    const parts = [];

    (itemsByBasket || []).forEach(basketGroup => {
        const isFastDelivery = basketGroup.basketType === BASKET_TYPES.SAMEDAY_BASKET;

        (basketGroup.items || []).forEach(item => {
            const skuId = item.sku?.skuId;

            if (!skuId) {
                return;
            }

            if (isFastDelivery) {
                parts.push(`;${skuId};;;;event323=${availableValue};eVar26=${skuId}`);
            } else {
                parts.push(`;${skuId};;;;eVar26=${skuId}`);
            }
        });
    });

    return parts.join(',');
}

export default (function () {
    const frictionlessCheckoutPageLoadEvent = function () {
        let defaultPaymentMethodName = 'empty';
        const creditCards = store.getState().order?.paymentOptions?.creditCards;
        const defaultCreditCard = creditCards?.filter(creditCard => creditCard.isDefault)?.[0];

        if (defaultCreditCard) {
            defaultPaymentMethodName = anaUtils.getPaymentCardType(defaultCreditCard?.cardType);
        }

        digitalData.transaction.attributes.defaultPayment = defaultPaymentMethodName?.toLowerCase();

        const userInfo = userUtils.getBiAccountInfo();

        if (userInfo?.promotionPoints) {
            digitalData.transaction.attributes.availableBiPoints = userInfo.promotionPoints;
        }

        const appliedPromoCodes = FrictionlessUtils.getPromosData().promoCodes;

        if (appliedPromoCodes && appliedPromoCodes.length > 0) {
            digitalData.cart.promoCodes = appliedPromoCodes;
        }

        // Note: event314 (delivery delay) is automatically added via getPageEvents() in generalBindings.js

        // AC1: Fast Delivery Available (event323) on frictionless checkout page load
        if (DeliveryOptionsUtils.isNDDEnabled()) {
            const fastDeliveryAvailableValue = getFastDeliveryAvailableValue();

            if (fastDeliveryAvailableValue !== null) {
                digitalData.page.attributes.eventStrings = (digitalData.page.attributes.eventStrings || []).concat([anaConsts.Event.EVENT_323]);
                digitalData.page.attributes.productStrings = buildFastDeliveryAvailableProductString(fastDeliveryAvailableValue);
            }
        }
    };

    return frictionlessCheckoutPageLoadEvent;
}());
